import React, { useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CLOTHESTYPE } from '../../../../enums';
import { ClothingService, FirefighterService } from '../../../../services';
import { useAppSelector } from '../../../../store/hooks';
import { selectIsAdmin, selectIsSuperAdmin } from '../../../../store/reducers';
import type { Clothing, FireDepartment, Firefighter, WashCycle } from '../../../../types';
import { SimpleBox } from '../../../atoms';

export function ClothingItem(): React.JSX.Element {
  const { clothItemId } = useParams();
  const isAdmin = useAppSelector(selectIsAdmin);
  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);

  const [isLoading, setIsLoading] = React.useState(true);
  const [clothing, setClothing] = React.useState<
    Clothing & { washCycles: WashCycle[]; firefighter: Firefighter | null; department: FireDepartment }
  >();
  const [members, setMembers] = React.useState<Firefighter[]>([]);
  const [selectedMember, setSelectedMember] = React.useState<string>('undefined');

  const loadFirefighters = async (departmentId: string): Promise<void> => {
    const firefighterResponse = await FirefighterService.getAllFirefightersOfDepartment(departmentId);
    if (firefighterResponse.error) {
      toast.error('Fehler beim Laden der Mitglieder 😩');
      console.log(firefighterResponse.message);

      return;
    }
    setMembers(firefighterResponse.data as Firefighter[]);
  };

  const loadClothingItemDetails = async () => {
    if (clothItemId) {
      const clothingItemResponse = await ClothingService.getClothingItemById(clothItemId);
      if (clothingItemResponse.error) {
        toast.error('Fehler beim Laden der Informationen des Kleidungsstückes 😩');
        console.error(clothingItemResponse.message);

        return;
      }

      const clothingData = clothingItemResponse.data as Clothing & {
        washCycles: WashCycle[];
        firefighter: Firefighter;
        department: FireDepartment;
      };

      setClothing(clothingData);

      if (clothingData.firefighter) {
        setSelectedMember(clothingData.firefighter.id);
      }

      setIsLoading(false);

      if (isSuperAdmin || isAdmin) {
        loadFirefighters(clothingData.department.id).then(() => {});
      }
    }
  };

  const assignClothingItemToMember = async () => {
    if (clothItemId && selectedMember !== 'undefined') {
      const response = await ClothingService.assignClothingItemToMember(clothItemId, selectedMember);

      if (response.error) {
        toast.error('Fehler beim Zuweisen des Kleidungsstückes 😩');
        console.error(response.message);

        return;
      }

      const memberIndex = members.findIndex((firefighter) => firefighter.id === selectedMember);
      const member = members[memberIndex];
      toast.success(`Kleidungsstück ${member.firstname} ${member.lastname} zugewiesen ✅`);

      const newClothingData = { ...clothing } as Clothing & {
        washCycles: WashCycle[];
        firefighter: Firefighter;
        department: FireDepartment;
      };
      newClothingData.firefighter = member;
      setClothing(newClothingData);
    }
  };

  const removeClothingItemFromMember = async () => {
    if (clothItemId) {
      const response = await ClothingService.removeClothingItemFromMember(clothItemId);

      if (response.error) {
        toast.error('Fehler beim Entfernen der Zuweisung des Kleidungsstückes 😩');
        console.error(response.message);

        return;
      }

      toast.success(`Zuweisung entfernt ✅`);

      const newClothingData = { ...clothing } as Clothing & {
        washCycles: WashCycle[];
        firefighter: Firefighter | null;
        department: FireDepartment;
      };
      newClothingData.firefighter = null;
      setClothing(newClothingData);
    }
  };

  const getClothingTypeName = (type: CLOTHESTYPE): string => {
    if (type === CLOTHESTYPE.JACKE) {
      return 'Jacke';
    }
    if (type === CLOTHESTYPE.AGT_HOSE) {
      return 'AGT-Hose';
    }
    if (type === CLOTHESTYPE.BUNDHOSE) {
      return 'Bundhose';
    }
    if (type === CLOTHESTYPE.LATZHOSE) {
      return 'Latzhose';
    }

    return 'XXXXXXX';
  };

  const getDate = (dateString: string): string => {
    const date = new Date(dateString);

    return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
  };

  useEffect(() => {
    loadClothingItemDetails().then(() => {});
  }, []);

  useEffect(() => {
    if (!isLoading && selectedMember !== clothing?.firefighter?.id) {
      if (selectedMember !== 'undefined') {
        assignClothingItemToMember().then(() => {});
      }
      if (selectedMember === 'undefined') {
        removeClothingItemFromMember().then(() => {});
      }
    }
  }, [selectedMember]);

  if (isLoading) {
    return <></>;
  }

  return (
    <div className={'container mt-10'}>
      {clothing && (
        <>
          <div className='row'>
            <div className='col-8'>
              <SimpleBox>
                <h4>Kleidungsstück</h4>
                <div>Typ: {getClothingTypeName(clothing.type)}</div>
                <div>Name: {clothing.name}</div>
                <div>Code: {clothing.code}</div>
              </SimpleBox>
            </div>
            <div className='col-4'>
              <SimpleBox>
                <h4>Infos</h4>
                <h6>Waschzyklen: {clothing.washCycles.length}</h6>
                {clothing.firefighter && (
                  <h6>
                    Mitglied: {clothing.firefighter.firstname} {clothing.firefighter.lastname}
                  </h6>
                )}
              </SimpleBox>
            </div>
          </div>
          {(isSuperAdmin || isAdmin) && (
            <div className='row mt-4'>
              <div className='col-12'>
                <SimpleBox>
                  <h4 className='mb-3'>Kleidung Mitglied zuweisen</h4>
                  <Form.Select value={selectedMember} onChange={(e) => setSelectedMember(e.target.value as string)}>
                    <option value='undefined'></option>
                    {members.map((member) => (
                      <option value={member.id}>
                        {member.firstname} {member.lastname}
                      </option>
                    ))}
                  </Form.Select>
                </SimpleBox>
              </div>
            </div>
          )}
          <div className='row mt-4'>
            <div className='col-12'>
              <SimpleBox>
                <h4>Waschzyklen</h4>
                <table className='table'>
                  <thead>
                    <tr>
                      <th scope='col'>#</th>
                      <th scope='col'>Datum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clothing.washCycles.map((washCycle, index) => (
                      <tr key={washCycle.id}>
                        <th scope='row'>{index + 1}</th>
                        <td>{getDate(washCycle.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </SimpleBox>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
